import React from 'react';
import PropTypes from 'prop-types';

import Select from '../../../../components/Select';
import { Button, BUTTON_TYPE_WHITE } from '../../../../components/Buttons/Button';
import { TextInput } from '../../../../components/Inputs/TextInput';
import Strings from '../../../../Strings';
import ScriptEditor, { scriptIsValid } from './ScriptEditor';
import {
  PATIENT_OPERATIONS,
  PATIENT_STATUSES,
  TRACKS,
  generateRandomMbi,
  scriptFrom,
  scriptSummary,
  toOptions,
} from './constants';
import styles from './AccessSimulator.module.scss';

// The draft edited by PatientModal: the patient row plus, per operation, the
// script being edited (draft.scripts) and the operations whose override was
// removed (draft.cleared). Only touched operations are written back, so opening
// a patient never rewrites its scripts.
export const emptyPatientDraft = () => ({
  mbi: '',
  display_name: '',
  track: null,
  status: 'new',
  scripts: {},
  cleared: [],
});

export const patientDraftFrom = patient => {
  const draft = emptyPatientDraft();
  if (!patient) return draft;
  return {
    ...draft,
    mbi: patient.mbi || '',
    display_name: patient.display_name || '',
    track: patient.track ?? null,
    status: patient.status || 'new',
  };
};

// The queued script of an operation (the queue head), or undefined when the
// patient inherits the organization default.
export const storedScript = (patient, operation) => patient?.responses?.find(
  response => response.operation === operation && response.seq === 0,
);

// Mirrors the checks of accessSimCreatePatient / accessSimSetPatientScript so
// Save cannot produce a 400.
export const patientDraftIsValid = draft => (
  !!draft.mbi.trim()
  && PATIENT_OPERATIONS.every(operation => !draft.scripts[operation] || scriptIsValid(draft.scripts[operation]))
);

/**
 * Add/edit dialog of a simulated patient: identity on top, then the scripted
 * answer of every ACCESS operation. An operation without
 * an override inherits the organization default, which is shown next to it.
 */
function PatientModal({ patient, defaults, orgPatients, draft, setDraft, isSaving }) {
  const isCreate = !patient;
  const defaultFor = operation => defaults.find(item => item.operation === operation);

  const setField = (field, value) => setDraft({ ...draft, [field]: value });

  // A "generate random MBI" helper (real 11-char MBI shape) and a
  // prefill from an existing org patient (whose MBI the simulator recognizes).
  const orgPatientOptions = (orgPatients || []).map(item => {
    const name = `${item.last_name}, ${item.first_name}`.trim();
    return { value: item.id, label: name ? `${name} · ${item.medicare_beneficiary_id}` : item.medicare_beneficiary_id };
  });

  const prefillFromOrgPatient = option => {
    const match = (orgPatients || []).find(item => item.id === option?.value);
    if (match) setField('mbi', match.medicare_beneficiary_id);
  };

  const editScript = (operation, script) => setDraft({
    ...draft,
    scripts: { ...draft.scripts, [operation]: script },
    cleared: draft.cleared.filter(item => item !== operation),
  });

  const clearScript = operation => setDraft({
    ...draft,
    scripts: { ...draft.scripts, [operation]: undefined },
    cleared: [...draft.cleared, operation],
  });

  const restoreScript = operation => setDraft({
    ...draft,
    cleared: draft.cleared.filter(item => item !== operation),
  });

  return (
    <div className={styles.patientModal}>
      <div className={styles.patientModalIdentity}>
        <label className={styles.patientModalField} htmlFor="access-simulator-patient-mbi">
          {Strings.accessSimMbi}
          <div className={styles.patientModalMbiRow}>
            <TextInput
              id="access-simulator-patient-mbi"
              type="text"
              value={draft.mbi}
              onChange={event => setField('mbi', event.target.value)}
              disabled={!isCreate || isSaving}
            />
            {isCreate && (
              <Button
                type="button"
                buttonType={BUTTON_TYPE_WHITE}
                onClick={() => setField('mbi', generateRandomMbi())}
                disabled={isSaving}
                data-testid="button[generate-mbi]"
              >
                {Strings.accessSimGenerateMbi}
              </Button>
            )}
          </div>
          {isCreate && !draft.mbi.trim() && <span className="validation-message">{Strings.required}</span>}
        </label>
        {isCreate && (
          <label className={styles.patientModalField} htmlFor="access-simulator-patient-org-patient">
            {Strings.accessSimOrgPatient}
            <Select
              id="access-simulator-patient-org-patient"
              label={Strings.accessSimOrgPatient}
              data={orgPatientOptions}
              value={null}
              onChange={prefillFromOrgPatient}
              clearValueAfterSelection
              isSearchable
              disabled={isSaving}
            />
            {orgPatientOptions.length === 0 && (
              <span className={styles.patientModalHint}>{Strings.accessSimNoOrgPatients}</span>
            )}
          </label>
        )}
        <label className={styles.patientModalField} htmlFor="access-simulator-patient-display-name">
          {Strings.accessSimDisplayName}
          <TextInput
            id="access-simulator-patient-display-name"
            type="text"
            value={draft.display_name}
            onChange={event => setField('display_name', event.target.value)}
            disabled={isSaving}
          />
        </label>
        <label className={styles.patientModalField} htmlFor="access-simulator-patient-track" data-tooltip-id="tooltip" data-tooltip-content={Strings.accessSimTrackTooltip}>
          {Strings.accessSimTrack}
          <Select
            id="access-simulator-patient-track"
            label={Strings.accessSimTrack}
            data={toOptions(TRACKS)}
            value={draft.track}
            onChange={option => setField('track', option?.value ?? null)}
            disabled={isSaving}
          />
        </label>
        <div className={styles.patientModalField}>
          <span data-tooltip-id="tooltip" data-tooltip-content={Strings.accessSimStatusTooltip}>{Strings.accessSimStatus}</span>
          <Select
            id="access-simulator-patient-status"
            label={Strings.accessSimStatus}
            data={toOptions(PATIENT_STATUSES)}
            value={draft.status}
            onChange={option => setField('status', option?.value)}
            disabled={isSaving}
          />
        </div>
      </div>

      <p className={styles.patientModalHint}>{Strings.accessSimPatientScriptHint}</p>

      {PATIENT_OPERATIONS.map(operation => {
        const stored = draft.cleared.includes(operation) ? null : (draft.scripts[operation] || storedScript(patient, operation));
        const inherited = defaultFor(operation);
        return (
          <div className={styles.patientModalScript} key={operation}>
            <div className={styles.patientModalScriptHeader}>
              <span className={styles.patientModalOperation}>{operation}</span>
              <span className={styles.patientModalInherited}>
                {stored
                  ? `${Strings.accessSimOverrideSet}: ${scriptSummary(stored)}`
                  : `${Strings.accessSimInherited}: ${scriptSummary(inherited) || '-'}`}
              </span>
              {stored && (
                <Button
                  type="button"
                  buttonType={BUTTON_TYPE_WHITE}
                  onClick={() => clearScript(operation)}
                  disabled={isSaving}
                  data-testid="button[clear-override]"
                >
                  {Strings.accessSimClearOverride}
                </Button>
              )}
              {draft.cleared.includes(operation) && (
                <Button
                  type="button"
                  buttonType={BUTTON_TYPE_WHITE}
                  onClick={() => restoreScript(operation)}
                  disabled={isSaving}
                >
                  {Strings.accessSimUseDefault}
                </Button>
              )}
            </div>
            <ScriptEditor
              id={`patient-${operation}`}
              operation={operation}
              value={scriptFrom(draft.scripts[operation], stored || inherited)}
              onChange={script => editScript(operation, script)}
              disabled={isSaving || draft.cleared.includes(operation)}
            />
          </div>
        );
      })}
    </div>
  );
}

PatientModal.propTypes = {
  patient: PropTypes.object,
  defaults: PropTypes.array,
  orgPatients: PropTypes.array,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

PatientModal.defaultProps = {
  patient: null,
  defaults: [],
  orgPatients: [],
};

export default PatientModal;
